import {
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ApiService, AuthService } from '@core/services';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class HeaderInterceptor implements HttpInterceptor {
  private static readonly PUBLIC_AUTH_PATHS = new Set([
    'auth/login/',
    'auth/token/refresh/',
    '2/passwordless-auth/',
    '2/auth/yandex/',
    '2/auth/telegram/',
    '2/auth/vk/',
  ]);

  private refreshTokenRequest$: Observable<string> | null = null;

  constructor(
    private auth: AuthService,
    private readonly _router: Router,
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const clone = this.addDefaultHeaders(req);

    if (this.shouldRefreshBeforeRequest(clone.url)) {
      return this.refreshTokenAndRetry(clone, next);
    }

    return next.handle(clone).pipe(
      catchError((error): Observable<HttpEvent<any>> => {
        const isApiRequest = this.isApiRequest(clone.url);
        if (
          error.status === 401 &&
          isApiRequest &&
          !this.isPublicAuthRequest(clone.url) &&
          this.auth.refreshToken
        ) {
          return this.refreshTokenAndRetry(clone, next);
        }

        if (error.status === 401 && isApiRequest && this.auth.isLogged) {
          this.handleUnauthorized();
        }

        return throwError(() => error);
      }),
    );
  }

  private addDefaultHeaders(req: HttpRequest<any>): HttpRequest<any> {
    let headers: HttpHeaders = req.headers;
    if (!headers.has('Content-Type') && !(req.body instanceof FormData)) {
      headers = headers.set('Content-Type', 'application/json');
    } else if (headers.get('Content-Type') === '') {
      headers = headers.delete('Content-Type');
    }
    if (!headers.has('Accept')) {
      headers = headers.set('Accept', 'application/json');
    }

    if (this.shouldAttachAuthorization(req.url)) {
      headers = headers.set('Authorization', `JWT ${this.auth.accessToken}`);
    }

    return req.clone({ headers });
  }

  private shouldAttachAuthorization(url: string): boolean {
    return this.isApiRequest(url) && !!this.auth.accessToken && !this.isPublicAuthRequest(url);
  }

  private shouldRefreshBeforeRequest(url: string): boolean {
    if (!this.isApiRequest(url) || this.isPublicAuthRequest(url) || !this.auth.refreshToken) {
      return false;
    }
    if (!this.auth.accessToken) {
      return true;
    }
    return this._isAccessTokenExpiredOrExpiringSoon(this.auth.accessToken);
  }

  private refreshTokenAndRetry(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    return this.getRefreshTokenRequest().pipe(
      switchMap((accessToken) => next.handle(this.addAuthorizationHeader(req, accessToken))),
      catchError((refreshError) => {
        this.handleUnauthorized();
        return throwError(() => refreshError);
      }),
    );
  }

  private getRefreshTokenRequest(): Observable<string> {
    if (!this.refreshTokenRequest$) {
      this.refreshTokenRequest$ = this.auth.refreshCurrentToken().pipe(
        map(() => {
          const accessToken = this.auth.accessToken;
          if (!accessToken) {
            throw new Error('Access token is missing after refresh');
          }

          return accessToken;
        }),
        finalize(() => {
          this.refreshTokenRequest$ = null;
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    }

    return this.refreshTokenRequest$;
  }

  private addAuthorizationHeader(req: HttpRequest<any>, token: string): HttpRequest<any> {
    return req.clone({
      headers: req.headers.set('Authorization', `JWT ${token}`),
    });
  }

  private isApiRequest(url: string): boolean {
    return url.indexOf(ApiService.apiUrl) === 0;
  }

  private isPublicAuthRequest(url: string): boolean {
    return HeaderInterceptor.PUBLIC_AUTH_PATHS.has(this.getApiPath(url));
  }

  private getApiPath(url: string): string {
    if (!this.isApiRequest(url)) {
      return '';
    }

    return url.slice(ApiService.apiUrl.length).split('?')[0];
  }

  private handleUnauthorized(): void {
    this.auth.logout();

    if (this._router.url !== '/login') {
      this._router.navigate(['/login']);
    }
  }

  private _isAccessTokenExpiredOrExpiringSoon(token: string, bufferSeconds = 30): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return typeof payload.exp === 'number' && payload.exp * 1000 < Date.now() + bufferSeconds * 1000;
    } catch {
      return true; // Если токен не удалось распарсить — считаем его истёкшим и пробуем обновить.
    }
  }
}
